import { MouseEvent, ReactElement, useState } from 'react';
import { useProductsAnalytics } from '@client/analytics/context/products/ProductsAnalyticsContext';
import { ADD_TO_CART_DISPLAY_TYPE, AddToCart } from '@client/cart/components/AddToCart';
import { CopyableText } from '@client/common/components/CopyableText';
import { HiddenPrice } from '@client/common/components/HiddenPrice/HiddenPrice';
import { StockCount } from '@client/common/components/StockCount';
import { useDeviceType } from '@client/common/hooks';
import { isMobile, isPhone } from '@client/common/utils/env';
import { useTranslate } from '@client/i18n/hooks';
import {
  ADD_TO_WISHLIST_STYLE_TYPE,
  AddToWishlistAction,
} from '@client/product/components/ProductActions/AddToWishlistAction';
import { ROUTES } from '@client/routes/components/Router/routes';
import { useOpenLink, useRouterPush, useUrlCreator } from '@client/routes/hooks';
import { getCategoryUrlParams, getCdnImageUrl, getProductFallbackImgUrl } from '@client/routes/utils/url';
import classNames from 'classnames';
import { Price, PRICE_SIZE } from 'b2b-components/theme/components/Price';
import {
  PRODUCT_LABEL_SIZE,
  PRODUCT_LABEL_STYLE_TYPE,
  ProductLabel,
} from 'b2b-components/theme/components/ProductLabel';
import { AddToCompareAction } from 'b2b-apps/core/product/components/ProductActions/AddToCompareAction';
import { Props } from './index';
import './AccessoryItem.scss';

export const AccessoryItem = ({ accessory, productSku, listPosition }: Props): ReactElement | null => {
  const { sku, category, effectivePricing, cover, title, settings } = accessory;
  const t = useTranslate();
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseOver = () => setIsHovered(true);
  const handleMouseOut = () => setIsHovered(false);
  const openLink = useOpenLink();
  const pushAction = useRouterPush();
  const urlCreator = useUrlCreator();
  const deviceType = useDeviceType();
  const { analyzeProductClick, analyzeProductAddToCart } = useProductsAnalytics();

  const handleCategoryClick = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    category
    && pushAction(
      `${urlCreator(ROUTES.category)}${getCategoryUrlParams(category.url)}`,
    );
  };

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();

    analyzeProductClick({ sku, listPosition, additionalId: productSku });
    openLink(
      event,
      urlCreator(ROUTES.product, { sku }),
    );
  };

  const handleAddToCart = () => {
    analyzeProductAddToCart({ sku, listPosition, additionalId: productSku });
  };

  return (
    <div
      className='AccessoryItem'
      key={sku}
      onMouseEnter={handleMouseOver}
      onMouseLeave={handleMouseOut}
    >
      <a
        className='AccessoryItem-ImageWrapper'
        href={urlCreator(ROUTES.product, { sku })}
        onClick={handleClick}
      >
        <img
          src={getCdnImageUrl(cover) || getProductFallbackImgUrl()}
          alt={title}
          title={title}
          className='cds-ImgFluid'
        />
      </a>

      <div className='AccessoryItem-Header'>
        <a
          href={urlCreator(ROUTES.product, { sku })}
          onClick={handleClick}
          className='AccessoryItem-HeaderLink'
        >
          {title}
        </a>
        {settings.isCoreAssortment && (
          <ProductLabel
            className='AccessoryItem-HeaderProductLabel'
            styleType={PRODUCT_LABEL_STYLE_TYPE.coreAssortment}
            size={PRODUCT_LABEL_SIZE.small}
            label={t('Core assortment')}
            isInline={true}
          />
        )}
      </div>
      <div className='AccessoryItem-Details'>
        <table>
          <tbody className='cds-ProductBox-Table cds-Table cds-Table--FeaturesSmall'>
          <tr className='cds-ProductBox-SKU'>
            <td className='cds-ProductBox-AttrLabel'>{`${t('SKU')}:`}</td>
            <td className='cds-ProductBox-AttrValue'>
              <CopyableText>{`${sku}`}</CopyableText>
            </td>
          </tr>

          {category && (
            <tr className='AccessoryItem-Category'>
              <td className='cds-ProductBox-AttrLabel'>{`${t('Category')}:`}</td>
              <td className='cds-ProductBox-AttrValue AccessoryItem-CategoryValue'>
                <a
                  className='cds-Link'
                  href={`${urlCreator(ROUTES.category)}${getCategoryUrlParams(
                    category.url,
                  )}`}
                  onClick={handleCategoryClick}
                >
                  {category.label}
                </a>
              </td>
            </tr>
          )}

          <tr>
            <td className='cds-ProductBox-AttrLabel'>{`${t('Stock')}:`}</td>
            <td className='cds-ProductBox-AttrValue'>
              <StockCount
                isSpecialOrder={settings.isSpecialOrder}
                stockCount={settings.quantity}
                quantityOrdered={settings.quantityOrdered}
                tooltipLabel={settings.isSpecialOrder ? t('S.O.') : undefined}
              />
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <div className='AccessoryItem-Price'>
        {effectivePricing.isHiddenPrice ? (
          <HiddenPrice
            className='AccessoryItem-HiddenPrice'
            isHiddenPriceAndNotCertified={
              effectivePricing.isHiddenPriceAndNotCertified
            }
          />
        ) : (
          <Price
            currency='CHF'
            price={effectivePricing.mainPrice}
            size={PRICE_SIZE.small}
          />
        )}
      </div>

      <div className='AccessoryItem-Actions'>
        <div className='AccessoryItem-Action'>
          {!isPhone(deviceType) && (
            <AddToCompareAction
              product={accessory}
              isVisible={isHovered || isMobile(deviceType)}
            />
          )}
        </div>

        <div className='AccessoryItem-Action'>
          <AddToWishlistAction
            skus={[sku]}
            styleType={ADD_TO_WISHLIST_STYLE_TYPE.icon}
            className={classNames({
              'cds-ProductBox-Action--Hidden': !isMobile(deviceType) && !isHovered,
            })}
          />
        </div>

        <div className='AccessoryItem-Action'>
          <AddToCart
            sku={sku}
            productName={title}
            settings={settings}
            type={ADD_TO_CART_DISPLAY_TYPE.icon}
            isUserCertified={effectivePricing.isCertified}
            onClick={handleAddToCart}
            isVisible={isHovered
              || (effectivePricing.isLoggedIn && !effectivePricing.isCertified)
              || isMobile(deviceType)}
            dataAttributes={{ 'data-testid': 'pdpAccessoriesTabAddToCartButton' }}
          />
        </div>
      </div>
    </div>
  );
};
